import {AbstractStore} from "./abstract-store";
import {action, autorun, computed, makeObservable, observable} from "mobx";

import {JsonSerializer, throwError} from "typescript-json-serializer";
import {Municipality, MunicipalityProps} from "../models/municipality";
import {ApiLayerConfiguration} from "../config/api-configuration";
import {MapState} from "../core/map-state";

export interface MunicipalityStoreProps {
    layerConfig: ApiLayerConfiguration;
    mapStore: MapState;
}

export class MunicipalityStore extends AbstractStore {

    mapStore: MapState;

    /**
     * Loaded items (local store)
     */
    items = observable.map<string, Municipality>();

    constructor(props: MunicipalityStoreProps) {
        super({ layerConfig: props.layerConfig });
        this.mapStore = props.mapStore;

        makeObservable(this, {
            status: observable,
            dataStatus: observable,

            items: observable,
            selectedItem: observable,
            setSelectedItem: action,
            resetSelectedItem: action,
            searchTerm: observable,
            filters: observable,
            sort: observable,
            visibleItems: computed,

            addItem: action,
            removeItem: action,

            isDataLoading: computed,
            isDataLoaded: computed,
            isDataError: computed,
            isLoading: computed,
            isLoaded: computed,
            isNotFound: computed,
            isError: computed
        });
    }

    get visibleItems(): Municipality[] {
        // apply filters
        const filtered = [...this.items.values()].filter(() => {
            return true;
        });
        // sort
        filtered.sort(Municipality.sortByName);
        return [ ...filtered];
    }

    /**
     * Override to use bfsNr as key
     * Add one or multiple items to the store
     * @param item One item or a map with multiple items with GUID as key
     */
    addItem(item: Municipality | Map<string, Municipality>): void {
        if (item instanceof Map) {
            // multiple items
            this.items.merge(item);
        }
        else {
            // one item
            if (this.items.has(String(item.bfsNr))) {
                this.items.delete(String(item.bfsNr));
            }
            this.items.set(String(item.bfsNr), item);
        }
    }

    /**
     * Override to use bfsNr as key
     * Remove an item from store
     * @param item
     */
    removeItem(item: Municipality): void {
        if (this.items.has(String(item.bfsNr))) {
            this.items.delete(String(item.bfsNr));
        }
    }

    /**
     * Override to use bfsNr as key
     * Get an item by GUID
     * @param bfsNr
     */
    getItem(bfsNr: string): Municipality | null {
        const item = this.items.get(bfsNr);
        return item || null;
    }

    loadItem(bfsNr: string | undefined): Promise<Municipality | null> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!bfsNr) {
                    reject("No BFS number provided.");
                    return;
                }

                this.queryFeatures([bfsNr]).then((municipalities: Municipality[]) => {
                    // BFS number is also stored in guid property
                    if (municipalities[0] && municipalities[0].bfsNr === bfsNr) {
                        const municipality = municipalities[0];
                        const loadedMunicipality = this.items.get(String(bfsNr));
                        if (loadedMunicipality) {
                            // return municipality as long as merging municipality is running.
                            // municipality can be displayed with
                            // indicator that synching is in municipality
                            // TODO: merge municipality with already loaded activity
                        }

                        this.addItem(municipality);
                        this.setSelectedItem(municipality);
                        this.setDataStatus("loaded");

                        resolve(municipality);
                    } else {
                        this.setDataStatus("error");
                        reject("Municipality not found.");
                        throw new Error("Municipality not found.");
                    }
                });
            });
        });
    }

    /**
     * Request municipalities from server and store in local store
     * @param bfsNrs (Optional) BFS numbers of municipalities
     */
    loadItems(bfsNrs?: string[]): Promise<Municipality[]> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                this.queryFeatures(bfsNrs).then((municipalities: Municipality[]) => {
                    if (municipalities) {
                        this.addMunicipalities(municipalities);
                        this.setDataStatus("loaded");

                        resolve(municipalities);
                    } else {
                        this.setDataStatus("error");
                        reject("Error requesting municipalities.");
                        throw new Error("Error requesting municipalities.");
                    }
                });
            });
        });
    }

    addMunicipalities(municipalities: Municipality[] | [] | null): void {
        // do only save to mobx when not requested with specified ids or contacts
        const map = new Map<string, Municipality>();
        municipalities?.forEach((m: Municipality) => {
            map.set(String(m.bfsNr), m);
        });
        this.addItem(map);
    }

    queryFeatures(municipalityBFSs?: string[], unique?: true): Promise<Municipality[] | [] | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                const queryParams = this.layer.createQuery();

                if (unique) {
                    queryParams.returnDistinctValues = true;
                }

                if (municipalityBFSs !== undefined) {
                    queryParams.where += ` AND BFS_NR IN (${municipalityBFSs.join(",")})`;
                }

                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        // eslint-disable-next-line max-len
                        const municipalities: Municipality[] = this.parseQueryResult(results) as Municipality[];
                        // municipalities are multipart features, so we expect to get multiple entries per municipality
                        // --> make unique for select options
                        const noDuplicates: Municipality[] = [...new Map(municipalities.map((m: Municipality) =>
                            [m.bfsNr, m])).values()];
                        resolve(noDuplicates);
                    } else {
                        reject("Error querying municipalities.");
                    }
                }, (error) => {
                    throw new Error(`Error querying municipality + ${error}`);
                });
            });
        });
    }

    queryFeaturesFromDb(municipalityBFSs?: string[],
                        geometry?: __esri.Geometry,
                        spatialRelationship?: "intersects"
                        | "contains"
                        | "crosses"
                        | "disjoint"
                        | "envelope-intersects"
                        | "index-intersects"
                        | "overlaps"
                        | "touches"
                        | "within"
                        | "relation",
                        distance?: number): Promise<Municipality[] | [] | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                const queryParams = this.layer.createQuery();
                if (geometry) {
                    queryParams.geometry = geometry;
                    queryParams.returnGeometry = true;
                }
                if (distance) {
                    queryParams.distance = 2;
                    queryParams.units = "meters";
                }
                if (spatialRelationship) {
                    queryParams.spatialRelationship = spatialRelationship;
                }
                if (municipalityBFSs !== undefined) {
                    queryParams.where += ` AND BFS_NR IN (${municipalityBFSs.join(",")})`;
                }

                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        // eslint-disable-next-line max-len
                        const municipalities: Municipality[] = this.parseQueryResult(results) as Municipality[];
                        // municipalities are multipart features, so we expect to get multiple entries per municipality
                        // --> make unique for select options
                        const noDuplicates: Municipality[] = [...new Map(municipalities.map((m: Municipality) =>
                            [m.bfsNr, m])).values()];
                        resolve(noDuplicates);
                    } else {
                        reject("Error querying municipalities.");
                    }
                }, (error) => {
                    throw new Error(`Error querying municipality + ${error}`);
                });
            });
        });
    }

    private parseQueryResult(data: __esri.FeatureSet): Municipality[] {
        const serializer = new JsonSerializer({
            errorCallback: throwError
        });

        const municipalities: Municipality[] = [];
        data.features?.forEach((feature: any) => {
            const json = serializer.deserialize(feature.attributes, Municipality);
            const m = new Municipality(json as MunicipalityProps);
            m.feature = feature;
            municipalities.push(m);
        });
        return municipalities;
    }

    zoomToItem(bfs: string): void {
        if (!bfs) {
            return;
        }
        this.loadItem(bfs).then((item: Municipality) => {
            this.mapStore?.view?.goTo(item.feature)
                .catch((error) => {
                    if (error.name !== "view:goto-interrupted") {
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }
                });
        });
    }

}
